/**
 * Box Builder — modular box state manager & UI components.
 *
 * Architecture:
 *   BoxBuilderState  — core state, persistence, Skio API (no DOM)
 *   BoxBuilderHeader — optional renderer for size/frequency/price controls
 *   BoxBuilderStickyBar — optional renderer for bottom sticky CTA + product dropdown
 *
 * State is persisted in localStorage so it survives page navigation.
 * Reusable on any page: include the data JSON + desired snippets + this script.
 */

const STORAGE_KEY = 'yrja-box-builder';
const SKIO_BOX_API = 'https://api.skio.com/storefront-http/get-classic-box-v2';

function normalizeSearchText(value) {
  return String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

function isEveryFourWeeksPlan(planName) {
  const normalized = normalizeSearchText(planName);
  return normalized.includes('4') && (
    normalized.includes('uke')
    || normalized.includes('uker')
    || normalized.includes('week')
    || normalized.includes('weeks')
  );
}

/**
 * Strip a trailing " xN" multiplier suffix that `_buildCartPayload` appends to
 * product names (e.g. "Burger 150 g x2" -> "Burger 150 g"). Cart properties
 * carry the line-total label, so we must remove it before treating the value
 * as a per-unit name during edit-restore.
 */
function stripQtySuffix(name) {
  return String(name == null ? '' : name).replace(/ x\d+$/, '');
}

/**
 * Format a per-unit kg value back into a short label like "1 kg" or "0.4 kg".
 * Used when reconstructing per-unit weight labels from cart line totals.
 */
function formatKgLabel(kg) {
  const n = Number(kg);
  if (!Number.isFinite(n) || n <= 0) return '';
  return n % 1 === 0 ? n + ' kg' : n.toFixed(1) + ' kg';
}

/**
 * POST /cart/change.js with quantity:0 to remove a cart line, awaiting and
 * parsing the response. Returns the updated cart JSON on success, or null on
 * error / unexpected response. Shopify returns 200 OK even when `id` does not
 * match an existing line, so callers must verify the response to know whether
 * removal actually happened.
 */
async function cartChangeRemoveLine(lineKey) {
  try {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({ id: lineKey, quantity: 0 }),
    });
    if (!res.ok) {
      console.error('BoxBuilder: /cart/change.js returned', res.status);
      return null;
    }
    const text = await res.text();
    if (text.trimStart().startsWith('<')) {
      console.error('BoxBuilder: HTML response from /cart/change.js');
      return null;
    }
    try {
      return JSON.parse(text);
    } catch (parseErr) {
      console.error('BoxBuilder: failed to parse /cart/change.js response', parseErr);
      return null;
    }
  } catch (err) {
    console.error('BoxBuilder: /cart/change.js request failed', err);
    return null;
  }
}

/**
 * Empty the cart of every ravareboks line. Used as a safety net so the cart
 * cannot retain a stale box if the targeted cartItemKey was wrong. Returns
 * true if no ravareboks lines remain in the cart after the operation.
 */
async function clearRavareboksLinesFromCart() {
  let cart;
  try {
    const res = await fetch('/cart.js', {
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    });
    if (!res.ok) return false;
    cart = await res.json();
  } catch (err) {
    console.error('BoxBuilder: failed to fetch /cart.js', err);
    return false;
  }
  const items = Array.isArray(cart?.items) ? cart.items : [];
  const ravareboksKeys = items
    .filter((item) => item && item.handle === 'ravareboks')
    .map((item) => item.key)
    .filter(Boolean);
  if (!ravareboksKeys.length) return true;
  for (const key of ravareboksKeys) {
    const updated = await cartChangeRemoveLine(key);
    if (!updated) return false;
    const stillPresent = Array.isArray(updated.items)
      && updated.items.some((item) => item && item.key === key);
    if (stillPresent) return false;
  }
  return true;
}

/**
 * Restore box builder state from a cart line item's embedded JSON data,
 * remove the item from cart (verified), write state to localStorage, and
 * navigate. Called by the "Endre" links on the cart page.
 *
 * Returns true on success. Returns false (without navigating) if the cart
 * line could not be confirmed removed; callers should re-enable the UI and
 * surface an error to the user.
 */
async function restoreBoxFromCart(data, navigateTo) {
  const dest = navigateTo || '/collections/alle-produkter';

  // 1. Build a canonical, per-unit state from the cart line.
  //    The cart property `Produkt N` carries a " xN" suffix when qty > 1, and
  //    `_vekt_N` / `_porsjoner_N` carry line totals — we must undo both so
  //    `_buildCartPayload` can re-multiply correctly without doubling.
  const slotCount = Number(data.slotCount);
  const rawItems = data.items || {};
  const rawNames = data.itemNames || {};
  const rawWeights = data.itemWeights || {};
  const rawPortions = data.itemPortions || {};

  let items = {};
  const itemNames = {};
  const itemWeights = {};
  const itemPortions = {};

  for (const [vid, qtyRaw] of Object.entries(rawItems)) {
    const qty = Number(qtyRaw) || 0;
    if (qty <= 0) continue;
    items[vid] = qty;

    if (rawNames[vid] != null) {
      itemNames[vid] = stripQtySuffix(rawNames[vid]);
    }

    const w = rawWeights[vid];
    if (w && Number(w.kg) > 0) {
      const perUnitKg = Number(w.kg) / qty;
      itemWeights[vid] = { kg: perUnitKg, label: formatKgLabel(perUnitKg) };
    } else {
      itemWeights[vid] = null;
    }

    const p = rawPortions[vid];
    if (p != null && p !== '' && Number(p) > 0) {
      itemPortions[vid] = Number(p) / qty;
    } else {
      itemPortions[vid] = null;
    }
  }

  // Enforce slot limit on the restored state: trim canonically if the cart
  // somehow stored more items than the box has slots.
  const totalQty = Object.values(items).reduce((s, q) => s + q, 0);
  if (Number.isFinite(slotCount) && slotCount > 0 && totalQty > slotCount) {
    const sortedEntries = Object.entries(items).sort((a, b) => {
      const aN = Number(a[0]);
      const bN = Number(b[0]);
      if (Number.isFinite(aN) && Number.isFinite(bN) && aN !== bN) return aN - bN;
      return String(a[0]).localeCompare(String(b[0]));
    });
    const trimmed = {};
    let acc = 0;
    for (const [vid, qty] of sortedEntries) {
      const take = Math.min(qty, slotCount - acc);
      if (take > 0) {
        trimmed[vid] = take;
        acc += take;
      }
      if (acc >= slotCount) break;
    }
    items = trimmed;
    const validKeys = new Set(Object.keys(items));
    for (const k of Object.keys(itemNames)) if (!validKeys.has(k)) delete itemNames[k];
    for (const k of Object.keys(itemWeights)) if (!validKeys.has(k)) delete itemWeights[k];
    for (const k of Object.keys(itemPortions)) if (!validKeys.has(k)) delete itemPortions[k];
  }

  const state = {
    variantId: String(data.variantId),
    slotCount: slotCount,
    items: items,
    itemNames: itemNames,
    itemWeights: itemWeights,
    itemPortions: itemPortions,
    sellingPlanId: String(data.sellingPlanId || ''),
  };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch { /* ignore */ }

  // 2. Remove the targeted ravareboks line. Best-effort here; the safety
  //    net below is the authoritative check.
  if (data.cartItemKey) {
    await cartChangeRemoveLine(data.cartItemKey);
  }

  // 3. Safety net: drop ANY remaining ravareboks lines and verify against
  //    /cart.js. If anything is still there we refuse to navigate.
  const cartCleared = await clearRavareboksLinesFromCart();
  if (!cartCleared) {
    // Roll back the localStorage write so the user is not left with a
    // half-applied edit state.
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
    return false;
  }

  // 4. Navigate to the builder page
  window.location.href = dest;
  return true;
}

/**
 * Render an expandable product detail card (matching box-product-card.liquid).
 * Used by the sticky bar and can be reused elsewhere in JS contexts.
 */
function renderBoxProductCard(name, weight, portions) {
  const items = [];
  if (weight) items.push(`<li>Totalvekt: ${weight}</li>`);
  if (portions && Number(portions) > 0) items.push(`<li>Antall porsjoner: ${portions}</li>`);
  return `
    <details class="box-product-card">
      <summary class="box-product-card__summary">
        <span class="box-product-card__name">${name}</span>
        <svg class="box-product-card__arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          <polyline points="6 9 12 15 18 9"></polyline>
        </svg>
      </summary>
      ${items.length ? `<ul class="box-product-card__list">${items.join('')}</ul>` : ''}
    </details>
  `;
}

/** Re-enable [data-box-edit] links that were disabled during a click. */
function reenableBoxEditLinks() {
  document.querySelectorAll('[data-box-edit]').forEach(el => {
    el.style.pointerEvents = '';
    el.style.opacity = '';
  });
}

/* ── Cart page: "Endre" click handler ── */
document.addEventListener('click', async (e) => {
  const editLink = e.target.closest('[data-box-edit]');
  if (!editLink) return;
  e.preventDefault();
  // Disable all edit links to prevent double-clicks
  document.querySelectorAll('[data-box-edit]').forEach(el => {
    el.style.pointerEvents = 'none';
    el.style.opacity = '0.5';
  });
  const cartItem = editLink.closest('.ravareboks-cart');
  if (!cartItem) { reenableBoxEditLinks(); return; }
  const dataEl = cartItem.querySelector('.box-edit-data');
  if (!dataEl) { reenableBoxEditLinks(); return; }
  const dest = editLink.getAttribute('href') || '/collections/alle-produkter';
  let data;
  try {
    data = JSON.parse(dataEl.textContent);
  } catch (err) {
    console.error('BoxBuilder: failed to parse cart state', err);
    reenableBoxEditLinks();
    alert('Kunne ikke lese boksinnholdet. Last siden p\u00e5 nytt og pr\u00f8v igjen.');
    return;
  }
  const ok = await restoreBoxFromCart(data, dest);
  if (!ok) {
    reenableBoxEditLinks();
    alert('Kunne ikke t\u00f8mme handlekurven. Last siden p\u00e5 nytt og pr\u00f8v igjen.');
  }
});

/** Extract numeric ID from a Shopify GID or plain numeric string */
function normalizeVariantId(id) {
  const str = String(id);
  const match = str.match(/(\d+)$/);
  return match ? match[1] : str;
}

/**
 * Hard per-variant quantity caps for the Råvareboks box builder, keyed by the
 * normalized (numeric) variant ID. Enforced per box/order in addition to any
 * per-variant limit returned by Skio — the stricter (smaller) cap always wins.
 * Use this for products that must be limited to N per customer order regardless
 * of the Skio configuration.
 */
const PER_VARIANT_MAX_QTY = {
  '51793815798072': 1,
};

/**
 * Resolve the hardcoded per-order cap for a variant, or null when the variant
 * has no hardcoded cap.
 */
function getHardVariantCap(variantId) {
  const cap = PER_VARIANT_MAX_QTY[normalizeVariantId(variantId)];
  return Number.isFinite(cap) && cap > 0 ? cap : null;
}

/**
 * User-facing per-customer limit label for a hard-capped variant (e.g.
 * "1 per kunde"), or null when the variant has no hardcoded cap.
 */
function getPerCustomerLabel(variantId) {
  const cap = getHardVariantCap(variantId);
  return cap != null ? `${cap} per kunde` : null;
}

// ═══════════════════════════════════════════════════════════════════════════════
// BoxBuilderState — headless core (state + API + persistence)
// ═══════════════════════════════════════════════════════════════════════════════

class BoxBuilderState {
  /**
   * @param {Object} config  – { productId, handle, variants[], sellingPlanGroups[] }
   *                           Falls back to reading #box-builder-data if omitted.
   */
  constructor(config) {
    if (!config) {
      const dataEl = document.getElementById('box-builder-data');
      if (!dataEl) return;
      config = JSON.parse(dataEl.textContent);
    }

    this.config = config;
    this.ready = true;
    this._listeners = [];
    this.selectableVariants = [];
    // Curated box container variants are one-time bundles and must never be
    // addable to the Råvareboks box builder.
    this.blockedVariantIds = this._collectBlockedVariantIds();

    this.state = this._loadState();

    // Validate stored variantId against current config (variants may have changed)
    const storedVariantValid = this.state.variantId
      && this.config.variants.some(v => String(v.id) === String(this.state.variantId));

    if (!storedVariantValid) {
      // Default to the 4-pack box ("Liten"); fall back to the smallest available size.
      let defaultVariant = this.config.variants.find((v) => Number(v.slotCount) === 4) || null;
      if (!defaultVariant) {
        this.config.variants.forEach((v) => {
          const slots = Number(v.slotCount);
          if (!Number.isFinite(slots) || slots <= 0) return;
          if (!defaultVariant || slots < Number(defaultVariant.slotCount)) defaultVariant = v;
        });
      }
      if (!defaultVariant) defaultVariant = this.config.variants[0];
      this.state.variantId = String(defaultVariant.id);
      this.state.slotCount = defaultVariant.slotCount;
      this.state.items = {};
      this.state.itemNames = {};
      this.state.itemWeights = {};
      this.state.itemPortions = {};
      // Default the delivery frequency to "Hver 4. uke" when starting fresh.
      const defaultPlan = this.resolveSellingPlanByIntent('every-4-weeks');
      this.state.sellingPlanId = defaultPlan ? String(defaultPlan.id) : '';
      this._saveState();
    } else {
      this.state.variantId = String(this.state.variantId);
      if (!this.state.itemNames) this.state.itemNames = {};
      if (!this.state.itemWeights) this.state.itemWeights = {};
      if (!this.state.itemPortions) this.state.itemPortions = {};
      // Clean up any orphan metadata from older state blobs and trim if the
      // stored state somehow exceeds the box's slotCount.
      this._enforceSlotLimit();
      this._saveState();
    }

    this._fetchBoxData();
  }

  // ── Public API ──────────────────────────────────────────────────────────

  /** Subscribe to state changes. Returns unsubscribe function. */
  onStateChange(fn) {
    this._listeners.push(fn);
    return () => {
      this._listeners = this._listeners.filter(l => l !== fn);
    };
  }

  getState() {
    return { ...this.state };
  }

  getConfig() {
    return this.config;
  }

  getSelectableVariants() {
    return this.selectableVariants;
  }

  /**
   * Collect curated-box container variant IDs present on the page (from
   * [data-curated-box-data] payloads). These are one-time bundle products and
   * must never be added as items inside the Råvareboks box builder.
   */
  _collectBlockedVariantIds() {
    const blocked = new Set();
    document.querySelectorAll('[data-curated-box-data]').forEach((el) => {
      try {
        const data = JSON.parse(el.textContent);
        if (data && data.containerId != null) {
          blocked.add(normalizeVariantId(data.containerId));
        }
      } catch { /* ignore malformed payloads */ }
    });
    return blocked;
  }

  itemCount() {
    return Object.values(this.state.items).reduce((sum, qty) => sum + qty, 0);
  }

  isFull() {
    return this.itemCount() >= this.state.slotCount;
  }

  resolveVariantBySlotCount(slotCount) {
    const target = Number(slotCount);
    return this.config.variants.find(
      (variant) => Number(variant.slotCount) === target && variant.available !== false
    ) || this.config.variants.find((variant) => Number(variant.slotCount) === target) || null;
  }

  resolveSellingPlanByIntent(intent) {
    const sellingPlans = (this.config.sellingPlanGroups || []).flatMap(
      (group) => group.sellingPlans || []
    );

    if (!intent) {
      return sellingPlans[0] || null;
    }

    if (intent === 'every-4-weeks') {
      return sellingPlans.find((plan) => isEveryFourWeeksPlan(plan.name)) || null;
    }

    const normalizedIntent = normalizeSearchText(intent);
    return sellingPlans.find((plan) => normalizeSearchText(plan.name) === normalizedIntent) || null;
  }

  applyPreset(preset) {
    const slotCount = Number(preset?.slotCount);
    if (!slotCount) {
      return { ok: false, error: 'Fant ikke en gyldig boksstørrelse for denne bestselgeren.' };
    }

    const boxVariant = this.resolveVariantBySlotCount(slotCount);
    if (!boxVariant) {
      return { ok: false, error: `Fant ikke en boksvariant med ${slotCount} produkter.` };
    }

    const sellingPlan = this.resolveSellingPlanByIntent(preset?.sellingPlanIntent);
    if (!sellingPlan) {
      return { ok: false, error: 'Fant ikke leveringsfrekvensen for hver 4. uke.' };
    }

    const rawItems = Array.isArray(preset?.items) ? preset.items.filter(Boolean) : [];
    const totalRequestedItems = rawItems.reduce((sum, item) => sum + Number(item.quantity || 1), 0);
    if (!rawItems.length || totalRequestedItems !== slotCount) {
      return { ok: false, error: 'Bestselgerboksen er ikke komplett. Prøv igjen.' };
    }

    const items = {};
    const itemNames = {};
    const itemWeights = {};
    const itemPortions = {};

    rawItems.forEach((item) => {
      const variantId = String(item.variantId || '');
      const quantity = Number(item.quantity || 1);
      if (!variantId || quantity <= 0) return;

      items[variantId] = (items[variantId] || 0) + quantity;

      if (item.name) {
        itemNames[variantId] = item.name;
      }

      if (item.weight && Number(item.weight.kg) > 0) {
        itemWeights[variantId] = {
          kg: Number(item.weight.kg),
          label: item.weight.label || '',
        };
      } else {
        itemWeights[variantId] = null;
      }

      if (item.portions !== null && item.portions !== undefined && item.portions !== '') {
        itemPortions[variantId] = Number(item.portions);
      } else {
        itemPortions[variantId] = null;
      }
    });

    if (!Object.keys(items).length) {
      return { ok: false, error: 'Kunne ikke bygge bestselgerboksen. Prøv igjen.' };
    }

    this.state = {
      variantId: String(boxVariant.id),
      slotCount,
      items,
      itemNames,
      itemWeights,
      itemPortions,
      sellingPlanId: String(sellingPlan.id),
    };
    this._saveState();
    this._notify();
    this._fetchBoxData();

    return {
      ok: true,
      variantId: String(boxVariant.id),
      sellingPlanId: String(sellingPlan.id),
    };
  }

  setSize(variantId, slotCount) {
    this.state.variantId = String(variantId);
    this.state.slotCount = slotCount;

    const currentCount = this.itemCount();
    if (currentCount > slotCount) {
      this._trimItems(slotCount);
    }

    this._saveState();
    this._notify();
    this._fetchBoxData();
  }

  setSellingPlan(sellingPlanId) {
    this.state.sellingPlanId = String(sellingPlanId || '');
    this._saveState();
    this._fetchBoxData();
  }

  /**
   * Effective per-variant quantity cap for the box, combining the hardcoded
   * PER_VARIANT_MAX_QTY caps with the per-variant limit returned by Skio. The
   * stricter (smaller) positive cap wins. Returns null when the variant is
   * unlimited.
   */
  getVariantMaxQty(variantId) {
    const caps = [];

    const hardCap = getHardVariantCap(variantId);
    if (hardCap != null) caps.push(hardCap);

    if (this.selectableVariants && this.selectableVariants.length > 0) {
      const normalizedId = normalizeVariantId(variantId);
      const selectable = this.selectableVariants.find(
        sv => normalizeVariantId(sv.platformId) === normalizedId
      );
      if (selectable && selectable.quantity) caps.push(Number(selectable.quantity));
    }

    if (!caps.length) return null;
    return Math.min(...caps);
  }

  /**
   * @param {string} variantId
   * @param {string} [productTitle] – optional; looked up from DOM if omitted
   * @returns {'added'|'full'|'max'} result code
   */
  addItem(variantId, productTitle) {
    const id = String(variantId);

    // Curated boxes are one-time bundles — never allow one into the box.
    if (this.blockedVariantIds && this.blockedVariantIds.has(normalizeVariantId(id))) {
      return 'blocked';
    }

    const currentCount = this.itemCount();

    if (currentCount >= this.state.slotCount) {
      return 'full';
    }

    // Enforce the per-variant max (hardcoded caps + Skio limit, stricter wins).
    const maxQty = this.getVariantMaxQty(id);
    if (maxQty != null) {
      const currentQty = this.state.items[id] || 0;
      if (currentQty >= maxQty) {
        return 'max';
      }
    }

    this.state.items[id] = (this.state.items[id] || 0) + 1;

    if (productTitle) {
      this.state.itemNames[id] = productTitle;
    } else if (!this.state.itemNames[id]) {
      this.state.itemNames[id] = this._getProductTitleFromDOM(variantId);
    }

    // Store weight per variant (read from DOM once)
    if (!this.state.itemWeights[id]) {
      this.state.itemWeights[id] = this._getProductWeightFromDOM(variantId);
    }

    // Store portions per variant (read from DOM once)
    if (this.state.itemPortions[id] === undefined) {
      this.state.itemPortions[id] = this._getProductPortionsFromDOM(variantId);
    }

    this._saveState();
    this._notify();
    return 'added';
  }

  removeItem(variantId) {
    const id = String(variantId);
    if (!this.state.items[id]) return;

    this.state.items[id] -= 1;
    if (this.state.items[id] <= 0) {
      // Drop the item AND its metadata so localStorage stays clean. If the
      // user re-adds the same variant later, addItem will refetch the
      // metadata from the DOM — the canonical sort in `_buildCartPayload`
      // means key insertion order does not affect the Shopify line hash.
      delete this.state.items[id];
      delete this.state.itemNames[id];
      delete this.state.itemWeights[id];
      delete this.state.itemPortions[id];
    }
    this._saveState();
    this._notify();
  }

  /**
   * Ensure the in-memory state never exceeds `slotCount` items and drop any
   * orphan metadata (entries in itemNames/itemWeights/itemPortions whose
   * variant is no longer in `items`). Safe to call repeatedly.
   */
  _enforceSlotLimit() {
    if (!this.state || !this.state.items) return;
    const slotCount = Number(this.state.slotCount);
    if (Number.isFinite(slotCount) && slotCount > 0 && this.itemCount() > slotCount) {
      this._trimItems(slotCount);
    }
    const validKeys = new Set(Object.keys(this.state.items));
    if (this.state.itemNames) {
      for (const k of Object.keys(this.state.itemNames)) {
        if (!validKeys.has(k)) delete this.state.itemNames[k];
      }
    }
    if (this.state.itemWeights) {
      for (const k of Object.keys(this.state.itemWeights)) {
        if (!validKeys.has(k)) delete this.state.itemWeights[k];
      }
    }
    if (this.state.itemPortions) {
      for (const k of Object.keys(this.state.itemPortions)) {
        if (!validKeys.has(k)) delete this.state.itemPortions[k];
      }
    }
  }

  _buildCartPayload() {
    const count = this.itemCount();
    if (count < this.state.slotCount) return null;

    const properties = {};
    const names = this.state.itemNames || {};
    const weights = this.state.itemWeights || {};
    const portions = this.state.itemPortions || {};
    let slotIndex = 1;
    let totalKg = 0;
    let totalPortions = 0;

    // Iterate items in canonical (sorted) order so the resulting properties
    // hash is independent of click order. This is what makes "minus then
    // plus the same product" produce the same Shopify line.
    const sortedEntries = Object.entries(this.state.items).sort((a, b) => {
      const aNum = Number(a[0]);
      const bNum = Number(b[0]);
      if (Number.isFinite(aNum) && Number.isFinite(bNum) && aNum !== bNum) {
        return aNum - bNum;
      }
      return String(a[0]).localeCompare(String(b[0]));
    });

    for (const [variantId, qty] of sortedEntries) {
      properties[`_pvgid://shopify/ProductVariant/${variantId}`] = qty;
      const name = names[variantId] || `Produkt ${variantId}`;
      properties[`Produkt ${slotIndex}`] = qty > 1 ? `${name} x${qty}` : name;

      // Weight per product line from variant option title (e.g. "1.1 kg")
      const w = weights[variantId];
      if (w && w.kg > 0) {
        const lineKg = w.kg * qty;
        totalKg += lineKg;
        properties[`_vekt_${slotIndex}`] = lineKg % 1 === 0 ? lineKg + ' kg' : lineKg.toFixed(1) + ' kg';
      }

      // Portions per product line
      const p = portions[variantId];
      if (p && p > 0) {
        const linePortions = p * qty;
        totalPortions += linePortions;
        properties[`_porsjoner_${slotIndex}`] = linePortions;
      }

      slotIndex++;
    }

    if (totalKg > 0) {
      properties['_totalvekt'] = totalKg % 1 === 0 ? totalKg + ' kg' : totalKg.toFixed(1) + ' kg';
    }

    if (totalPortions > 0) {
      properties['_totalporsjoner'] = totalPortions;
    }

    const payload = {
      id: Number(this.state.variantId),
      quantity: 1,
      properties,
    };

    if (this.state.sellingPlanId) {
      payload.selling_plan = Number(this.state.sellingPlanId);
    }

    return payload;
  }

  async _sendCartAddRequest(payload, options = {}) {
    const requestBody = { ...payload };

    if (Array.isArray(options.sections) && options.sections.length > 0) {
      requestBody.sections = options.sections;
      requestBody.sections_url = options.sectionsUrl || window.location.pathname;
    }

    // Ensure we hit the .js AJAX endpoint (returns JSON, not HTML)
    let url = (window.routes && window.routes.cart_add_url) || '/cart/add.js';
    if (!url.endsWith('.js')) url += '.js';

    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(requestBody),
      });

      // Read body as text first so we can inspect it before parsing
      const text = await res.text();

      // Guard against HTML responses (e.g. redirect followed by fetch)
      if (text.trimStart().startsWith('<')) {
        console.error('BoxBuilder: HTML response from cart add', res.status, text.slice(0, 300));
        this._lastError = { status: res.status, message: 'Serveren returnerte et uventet svar. Prøv igjen.' };
        return false;
      }

      let data;
      try {
        data = JSON.parse(text);
      } catch (parseErr) {
        console.error('BoxBuilder: failed to parse cart response', res.status, text.slice(0, 300));
        this._lastError = { status: res.status, message: 'Kunne ikke lese svaret fra serveren.' };
        return false;
      }

      if (!res.ok || data.status) {
        this._lastError = data;
        console.error(`BoxBuilder: cart add failed (${res.status})`, data);
        return false;
      }

      if (options.clearState) {
        this._clearState();
      }
      return data;
    } catch (err) {
      console.error('BoxBuilder: cart add error', err);
      this._lastError = { message: err.message };
      return false;
    }
  }

  async addToCart() {
    // Belt-and-braces: make sure the box never exceeds its slot count and
    // that metadata has no orphans before we serialise it for Shopify.
    this._enforceSlotLimit();
    this._saveState();
    const payload = this._buildCartPayload();
    if (!payload) return false;

    // Defense in depth: before adding the new box, make sure the cart
    // does not already contain a ravareboks line. The "Endre" flow should
    // have removed it, but a stale cartItemKey or a flaky network could
    // leave one behind. Removing here keeps the cart at exactly one box.
    const cleared = await clearRavareboksLinesFromCart();
    if (!cleared) {
      this._lastError = { message: 'Kunne ikke t\u00f8mme handlekurven f\u00f8r ny boks ble lagt til. Last siden p\u00e5 nytt og pr\u00f8v igjen.' };
      return false;
    }

    const result = await this._sendCartAddRequest(payload, { clearState: true });
    if (!result) return false;

    // Do NOT call _notify() here — the UI should stay in its current
    // state while the page redirects to /cart. Clearing localStorage
    // is enough; visual re-render is handled by the caller.
    return true;
  }

  async addToCartWithSections(options = {}) {
    this._enforceSlotLimit();
    this._saveState();
    const payload = this._buildCartPayload();
    if (!payload) return false;

    // Preset flow allows multiple distinct ravareboks lines in the cart,
    // so the caller must opt in to the cleanup.
    if (options.clearAllRavareboksLines) {
      const cleared = await clearRavareboksLinesFromCart();
      if (!cleared) {
        this._lastError = { message: 'Kunne ikke t\u00f8mme handlekurven f\u00f8r ny boks ble lagt til. Last siden p\u00e5 nytt og pr\u00f8v igjen.' };
        return false;
      }
    }

    return this._sendCartAddRequest(payload, {
      sections: options.sections,
      sectionsUrl: options.sectionsUrl,
      clearState: Boolean(options.clearState),
    });
  }

  destroy() {
    this._listeners = [];
  }

  // ── Internal ────────────────────────────────────────────────────────────

  _notify() {
    const snapshot = this.getState();
    this._listeners.forEach(fn => fn(snapshot));
  }

  _getProductTitleFromDOM(variantId) {
    const input = document.querySelector(
      `input.product-variant-id[value="${variantId}"], input[name="id"][value="${variantId}"]`
    );
    if (!input) return 'Ukjent produkt';
    const card = input.closest('product-info, .card-main-wrapper');
    if (!card) return 'Ukjent produkt';
    const heading = card.querySelector('.card__heading a, .card__heading');
    return heading ? heading.textContent.trim() : 'Ukjent produkt';
  }

  /**
   * Read product weight from the variant option title (e.g. "1.1 kg").
   * Looks up the variant in window.cart_items which is keyed by product handle.
   * Returns { kg: Number, label: String } or null if not available.
   */
  _getProductWeightFromDOM(variantId) {
    const id = Number(variantId);
    if (window.cart_items) {
      for (const handle in window.cart_items) {
        const variants = window.cart_items[handle];
        if (!Array.isArray(variants)) continue;
        const variant = variants.find(v => v.id === id);
        if (variant && variant.title) {
          const match = variant.title.match(/([\d,.]+)\s*(kg|g)/i);
          if (match) {
            let kg = parseFloat(match[1].replace(',', '.'));
            if (match[2].toLowerCase() === 'g') kg /= 1000;
            return { kg, label: variant.title.trim() };
          }
        }
      }
    }
    return null;
  }

  /**
   * Read portion count from the data-box-variant-porsjoner attribute on the
   * product card's box-mode wrapper element.
   * Returns a Number or null if not available.
   */
  _getProductPortionsFromDOM(variantId) {
    const wrapper = document.querySelector(
      `[data-box-variant-id="${variantId}"][data-box-variant-porsjoner]`
    );
    if (wrapper) {
      const val = parseInt(wrapper.dataset.boxVariantPorsjoner, 10);
      if (!isNaN(val) && val > 0) return val;
    }
    return null;
  }

  _trimItems(maxCount) {
    // Iterate in canonical (sorted) order so the result is deterministic.
    const entries = Object.entries(this.state.items).sort((a, b) => {
      const aN = Number(a[0]);
      const bN = Number(b[0]);
      if (Number.isFinite(aN) && Number.isFinite(bN) && aN !== bN) return aN - bN;
      return String(a[0]).localeCompare(String(b[0]));
    });
    let count = 0;
    const trimmed = {};
    for (const [id, qty] of entries) {
      const take = Math.min(qty, maxCount - count);
      if (take > 0) {
        trimmed[id] = take;
        count += take;
      }
      if (count >= maxCount) break;
    }
    this.state.items = trimmed;
  }

  async _fetchBoxData() {
    try {
      const body = { productVariantPlatformId: this.state.variantId };
      if (this.state.sellingPlanId) {
        body.sellingPlanPlatformId = this.state.sellingPlanId;
      }

      const res = await fetch(SKIO_BOX_API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      const data = await res.json();
      if (!data.ok) {
        console.warn('BoxBuilder: Skio API returned not ok', data);
        return;
      }

      const box = data.ClassicBoxV2;
      this.selectableVariants = box.selectableProductVariants || [];

      if (box.size && box.size !== this.state.slotCount) {
        this.state.slotCount = box.size;
        // Skio may have shrunk the box; trim and clean orphan metadata to
        // keep the state consistent with the new slot count.
        this._enforceSlotLimit();
        this._saveState();
        this._notify();
      }

      this._updateCardSelectability();
    } catch (err) {
      console.error('BoxBuilder: failed to fetch box data', err);
    }
  }

  _updateCardSelectability() {
    if (!this.selectableVariants.length) return;
    document.querySelectorAll('product-info[data-product-id]').forEach(card => {
      const btn = card.querySelector('.quick-add__submit, .add-to-cart__submit, .product-form__submit');
      if (btn) btn.removeAttribute('title');
    });
  }

  _loadState() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) return JSON.parse(raw);
    } catch { /* ignore */ }
    return {
      variantId: null,
      slotCount: 4,
      items: {},
      itemNames: {},
      itemWeights: {},
      itemPortions: {},
      sellingPlanId: '',
    };
  }

  _saveState() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.state));
    } catch { /* ignore */ }
  }

  _clearState() {
    this.state = {
      variantId: null,
      slotCount: 4,
      items: {},
      itemNames: {},
      itemWeights: {},
      itemPortions: {},
      sellingPlanId: '',
    };
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
  }
}

// ═══════════════════════════════════════════════════════════════════════════════
// BoxBuilderHeader — renders size tabs, frequency, price (top of collection)
// ═══════════════════════════════════════════════════════════════════════════════

class BoxBuilderHeader {
  constructor(store) {
    this.store = store;
    this.el = document.getElementById('box-builder-header');
    if (!this.el) return;

    // Native size select + hint
    this.sizeEl = this.el.querySelector('[data-box-size]');
    this.sizeHint = this.el.querySelector('[data-box-size-hint]');

    // Native frequency select
    this.frequencyEl = this.el.querySelector('[data-box-frequency]');

    this._bindEvents();
    this._syncFromState();

    this._unsub = store.onStateChange(() => this._syncFromState());
  }

  _bindEvents() {
    // Size dropdown
    if (this.sizeEl) {
      const savedVariant = this.store.getState().variantId;
      if (savedVariant) {
        this.sizeEl.value = savedVariant;
      } else {
        const selectedOption = this.sizeEl.options[this.sizeEl.selectedIndex];
        const slotCount = parseInt(selectedOption.dataset.slotCount, 10);
        this.store.setSize(this.sizeEl.value, slotCount);
      }
      this._updateSizeHint();
      this.sizeEl.addEventListener('change', () => {
        const selectedOption = this.sizeEl.options[this.sizeEl.selectedIndex];
        const slotCount = parseInt(selectedOption.dataset.slotCount, 10);
        this.store.setSize(this.sizeEl.value, slotCount);
        this._updateSizeHint();
      });
    }

    // Frequency dropdown
    if (this.frequencyEl) {
      const savedPlan = this.store.getState().sellingPlanId;
      if (savedPlan) {
        this.frequencyEl.value = savedPlan;
      } else {
        this.store.setSellingPlan(this.frequencyEl.value);
      }
      this.frequencyEl.addEventListener('change', () => {
        this.store.setSellingPlan(this.frequencyEl.value);
      });
    }
  }

  _updateSizeHint() {
    if (!this.sizeEl || !this.sizeHint) return;
    const opt = this.sizeEl.options[this.sizeEl.selectedIndex];
    const dynamic = opt?.dataset.hint || '';
    const suffix = this.sizeHint.dataset.sizeHintSuffix
      || 'R\u00e5varepakker varierer i st\u00f8rrelse, se utvalget nedenfor.';
    const shipping = this.sizeHint.dataset.shippingHint || 'Gratis levering.';
    this.sizeHint.textContent = dynamic ? `${dynamic} ${suffix}` : suffix;
    this.sizeHint.appendChild(document.createElement('br'));
    this.sizeHint.appendChild(document.createTextNode(shipping));
  }

  /** Sync both native selects to match the current store state (e.g. after restore from localStorage) */
  _syncFromState() {
    const state = this.store.getState();

    if (this.sizeEl && this.sizeEl.value !== String(state.variantId)) {
      this.sizeEl.value = String(state.variantId);
      this._updateSizeHint();
    }

    if (this.frequencyEl && state.sellingPlanId && this.frequencyEl.value !== String(state.sellingPlanId)) {
      this.frequencyEl.value = String(state.sellingPlanId);
    }
  }

  destroy() {
    if (this._unsub) this._unsub();
  }
}

// ═══════════════════════════════════════════════════════════════════════════════
// BoxBuilderStickyBar — bottom-fixed CTA button with progress + product dropdown
// ═══════════════════════════════════════════════════════════════════════════════

class BoxBuilderStickyBar {
  constructor(store) {
    this.store = store;
    this.el = document.getElementById('box-builder-sticky-bar');
    if (!this.el) return;

    this.btn = this.el.querySelector('[data-sticky-checkout]');
    this.btnText = this.el.querySelector('[data-sticky-text]');
    this.btnFill = this.el.querySelector('[data-sticky-fill]');
    this.toggle = this.el.querySelector('[data-sticky-toggle]');
    this.toggleText = this.el.querySelector('[data-sticky-toggle-text]');
    this.dropdown = this.el.querySelector('[data-sticky-dropdown]');
    this.itemList = this.el.querySelector('[data-sticky-items]');
    this.grip = this.el.querySelector('[data-sticky-grip]');
    this.inner = this.el.querySelector('.sticky-bar__inner');
    this.row = this.el.querySelector('.sticky-bar__row');
    this.isOpen = false;
    this._navigating = false;

    this._bindEvents();
    this._bindSwipeGesture();
    this._bindFooterBoundary();
    this._render();

    this._unsub = store.onStateChange(() => this._render());
  }

  _bindEvents() {
    if (this.btn) {
      this.btn.addEventListener('click', async () => {
        if (!this.store.isFull()) return;
        this.btn.disabled = true;

        // Show loading text while the request is in flight
        if (this.btnText) this.btnText.textContent = 'Legger til\u2026';

        const ok = await this.store.addToCart();
        if (ok) {
          // Prevent any further re-renders from resetting the UI
          this._navigating = true;
          window.location.href = '/cart';
        } else {
          const errMsg = this.store._lastError?.description || this.store._lastError?.message || '';
          alert('Kunne ikke legge boksen i handlekurven.' + (errMsg ? '\n' + errMsg : ' Prøv igjen.'));
          this.btn.disabled = false;
          this._render();
        }
      });
    }

    if (this.toggle) {
      this.toggle.addEventListener('click', (e) => {
        e.stopPropagation();
        this.isOpen = !this.isOpen;
        this._renderDropdown();
      });
    }

    // Close dropdown when clicking outside
    document.addEventListener('click', (e) => {
      if (this.isOpen && this.el && !this.el.contains(e.target)) {
        this.isOpen = false;
        this._renderDropdown();
      }
    });
  }

  _render() {
    if (this._navigating) return;
    const count = this.store.itemCount();
    const state = this.store.getState();
    const total = state.slotCount;
    const pct = Math.min((count / total) * 100, 100);
    const isFull = count >= total;

    // Progress fill
    if (this.btnFill) {
      this.btnFill.style.width = `${pct}%`;
    }

    // Button text
    if (this.btnText) {
      this.btnText.textContent = isFull
        ? 'Legg boks i handlekurv'
        : `${count}/${total} r\u00e5varepakker valgt`;
    }

    // Button state
    if (this.btn) {
      this.btn.classList.toggle('sticky-bar__btn--full', isFull);
      this.btn.disabled = !isFull;
    }


    // Re-render dropdown items if open
    if (this.isOpen) {
      this._renderItems();
    }

  }

  _renderDropdown() {
    if (this.dropdown) {
      this.dropdown.classList.toggle('sticky-bar__dropdown--open', this.isOpen);
    }
    if (this.toggle) {
      this.toggle.classList.toggle('sticky-bar__toggle--open', this.isOpen);
      this.toggle.setAttribute('aria-expanded', String(this.isOpen));
    }
    if (this.toggleText) {
      this.toggleText.textContent = this.isOpen ? 'Skjul innhold' : 'Se/endre innhold';
    }
    if (this.grip) {
      this.grip.classList.toggle('sticky-bar__grip--visible', this.isOpen);
    }

    if (this.isOpen) {
      this._renderItems();
    }
  }

  _renderItems() {
    if (!this.itemList) return;

    const state = this.store.getState();
    const items = state.items;
    const names = state.itemNames || {};
    const weights = state.itemWeights || {};
    const portions = state.itemPortions || {};

    if (Object.keys(items).length === 0) {
      this.itemList.innerHTML = '<li class="sticky-bar__empty">Ingen r\u00e5varepakker lagt til enn\u00e5</li>';
      return;
    }

    // Remember which items were expanded before re-render
    const openVariants = new Set();
    this.itemList.querySelectorAll('.sticky-bar__item-details[open]').forEach(d => {
      const li = d.closest('[data-variant-id]');
      if (li) openVariants.add(li.dataset.variantId);
    });

    this.itemList.innerHTML = Object.entries(items).map(([variantId, qty]) => {
      const name = names[variantId] || `Produkt ${variantId}`;
      const w = weights[variantId];
      const p = portions[variantId];
      const lineKg = w && w.kg ? (w.kg * qty) : 0;
      const lineKgStr = lineKg > 0 ? (lineKg % 1 === 0 ? lineKg + ' kg' : lineKg.toFixed(1) + ' kg') : null;
      const linePortions = p ? p * qty : 0;
      const isOpen = openVariants.has(variantId) ? ' open' : '';

      let detailItems = '';
      if (lineKgStr) detailItems += `<li>– Totalvekt: ${lineKgStr}</li>`;
      if (linePortions > 0) detailItems += `<li>– Antall porsjoner: ${linePortions}</li>`;

      return `
        <li class="sticky-bar__item" data-variant-id="${variantId}">
          <details class="sticky-bar__item-details"${isOpen}>
            <summary class="sticky-bar__item-row">
              <div class="sticky-bar__item-controls" data-no-toggle>
                <button type="button" class="sticky-bar__item-btn" data-action="remove" data-variant="${variantId}" aria-label="Fjern én">−</button>
                <span class="sticky-bar__item-qty">${qty}</span>
                <button type="button" class="sticky-bar__item-btn" data-action="add" data-variant="${variantId}" aria-label="Legg til én">+</button>
              </div>
              <span class="sticky-bar__item-name"><span class="sticky-bar__item-name-text">${name}</span></span>
              <svg class="sticky-bar__item-arrow" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg>
            </summary>
            ${detailItems ? `<div class="sticky-bar__item-detail-wrap"><ul class="sticky-bar__item-detail-list">${detailItems}</ul></div>` : ''}
          </details>
        </li>
      `;
    }).join('');

    // Prevent +/- buttons from toggling the <details>
    this.itemList.querySelectorAll('[data-no-toggle]').forEach(controls => {
      controls.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
      });
    });

    // Bind +/- buttons
    this.itemList.querySelectorAll('[data-action]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const vid = btn.dataset.variant;
        if (btn.dataset.action === 'add') {
          const result = this.store.addItem(vid);
          if (result === 'full') this._flashBtn('Boksen er full!');
          if (result === 'max') this._flashBtn('Maks antall nådd');
        } else {
          this.store.removeItem(vid);
        }
      });
    });
  }

  _flashBtn(message) {
    if (!this.btnText) return;
    this.btnText.textContent = message;
    setTimeout(() => this._render(), 1200);
  }

  // _renderCardBadges removed — quantity is now shown via the [−] qty [+]
  // controls rendered by BoxBuilderFormInterceptor below each product card.

  /** Mobile swipe-to-expand/collapse gesture on the grip handle */
  _bindSwipeGesture() {
    const target = this.grip || this.el;
    if (!target) return;

    let startY = 0;
    let dragging = false;

    target.addEventListener('touchstart', (e) => {
      // Only capture on the grip or the bar itself, not on scrollable dropdown items
      if (this.isOpen && this.itemList && this.itemList.contains(e.target)) return;
      startY = e.touches[0].clientY;
      dragging = true;
      // Disable transition during drag for immediate feedback
      if (this.dropdown) this.dropdown.style.transition = 'none';
    }, { passive: true });

    target.addEventListener('touchmove', (e) => {
      if (!dragging) return;
      // Prevent page scroll while dragging on the grip
      if (this.grip && this.grip.contains(e.target)) {
        e.preventDefault();
      }
      const currentY = e.touches[0].clientY;
      const deltaY = currentY - startY;
      // When open, dragging down — whole bar slides down, buttons counter-translate to stay put
      if (this.isOpen && deltaY > 0) {
        if (this.el) this.el.style.transform = `translateY(${deltaY}px)`;
        if (this.row) this.row.style.transform = `translateY(${-deltaY}px)`;
      }
      // When closed, dragging up expands — no visual feedback needed (grip hidden)
    }, { passive: false });

    target.addEventListener('touchend', (e) => {
      if (!dragging) return;
      dragging = false;
      // Reset inline styles
      if (this.el) this.el.style.transform = '';
      if (this.row) this.row.style.transform = '';
      if (this.dropdown) this.dropdown.style.transition = '';
      const endY = e.changedTouches[0].clientY;
      const deltaY = endY - startY;

      // Swipe up → expand, swipe down → collapse (50px threshold)
      if (deltaY < -50 && !this.isOpen) {
        this.isOpen = true;
        this._renderDropdown();
      } else if (deltaY > 50 && this.isOpen) {
        this.isOpen = false;
        this._renderDropdown();
      }
    }, { passive: true });
  }

  /** Stop the sticky bar from overlapping the footer */
  _bindFooterBoundary() {
    if (!this.el) return;
    this._footer = document.querySelector('#shopify-section-footer-group, footer.footer');
    if (!this._footer) {
      // Fallback: try the first <footer> on the page
      this._footer = document.querySelector('footer');
    }
    if (!this._footer) return;

    this._onScroll = () => {
      const footerTop = this._footer.getBoundingClientRect().top;
      const viewportH = window.innerHeight;
      if (footerTop < viewportH) {
        this.el.style.bottom = (viewportH - footerTop) + 'px';
      } else {
        this.el.style.bottom = '0';
      }
    };

    window.addEventListener('scroll', this._onScroll, { passive: true });
    window.addEventListener('resize', this._onScroll, { passive: true });
    this._onScroll();
  }

  destroy() {
    if (this._unsub) this._unsub();
    if (this._onScroll) {
      window.removeEventListener('scroll', this._onScroll);
      window.removeEventListener('resize', this._onScroll);
    }
  }
}

// ═══════════════════════════════════════════════════════════════════════════════
// BoxBuilderFormInterceptor
// renders inline [−] qty [+] controls when items are in the box.
// ═══════════════════════════════════════════════════════════════════════════════

class BoxBuilderFormInterceptor {
  constructor(store) {
    this.store = store;
    this._syncing = false;

    this._onSubmit = (e) => {
      const form = e.target;
      if (!form.matches('[data-type="add-to-cart-form"]')) return;
      if (!document.getElementById('box-builder-header') && !document.getElementById('box-builder-sticky-bar')) return;
      // Curated boxes are direct-purchase bundles — let them add to cart, never to the box.
      if (form.closest('[data-allow-direct-purchase]')) return;

      e.preventDefault();
      e.stopPropagation();

      const variantInput = form.querySelector('.product-variant-id, [name="id"]');
      if (!variantInput) return;

      const result = this.store.addItem(variantInput.value);
      if (result === 'full' || result === 'max') {
        this._flashButton(variantInput.value, result);
      }
    };

    this._onClick = (e) => {
      const btn = e.target.closest('.quick-add__submit, .add-to-cart__submit, .product-form__submit');
      if (!btn) return;
      if (!document.getElementById('box-builder-header') && !document.getElementById('box-builder-sticky-bar')) return;

      const form = btn.closest('form');
      if (!form) return;
      // Curated boxes are direct-purchase bundles — let them add to cart, never to the box.
      if (form.closest('[data-allow-direct-purchase]')) return;

      e.preventDefault();
      e.stopPropagation();

      const variantInput = form.querySelector('.product-variant-id, [name="id"]');
      if (!variantInput) return;

      const result = this.store.addItem(variantInput.value);
      if (result === 'full' || result === 'max') {
        this._flashButton(variantInput.value, result);
      }
    };

    document.addEventListener('submit', this._onSubmit);
    document.addEventListener('click', this._onClick, true);

    // Sync all cards on every state change
    this._unsub = store.onStateChange(() => this._syncAllCards());

    // Initial sync (restores +/- controls for items persisted in localStorage)
    this._syncAllCards();

    // Re-sync when facet filtering replaces the product grid HTML
    this._observeGrid();
  }

  // ── Sync all [data-box-variant-id] containers on the page ──────────────

  _syncAllCards() {
    this._syncing = true;
    const state = this.store.getState();

    document.querySelectorAll('[data-box-variant-id]').forEach(container => {
      const variantId = container.dataset.boxVariantId;
      const qty = state.items[variantId] || 0;

      this._syncPerCustomerLabel(container, variantId);

      if (qty > 0) {
        this._showQtyControl(container, variantId, qty);
      } else {
        this._hideQtyControl(container);
      }
    });

    requestAnimationFrame(() => { this._syncing = false; });
  }

  /**
   * Render a top-left "N per kunde" label on the product card for variants that
   * carry a hardcoded per-customer cap (PER_VARIANT_MAX_QTY). Idempotent — safe
   * to call on every sync.
   */
  _syncPerCustomerLabel(container, variantId) {
    const label = getPerCustomerLabel(variantId);
    if (!label) return;

    const card = container.closest('.card');
    const media = card ? card.querySelector('.card__media') : null;
    if (!media) return;

    let el = media.querySelector('.box-per-customer-label');
    if (!el) {
      el = document.createElement('span');
      el.className = 'box-per-customer-label';
      media.appendChild(el);
    }
    el.textContent = label;
  }

  _showQtyControl(container, variantId, qty) {
    // Hide the original product-form
    const form = container.querySelector('product-form');
    if (form) form.style.display = 'none';

    const isFull = this.store.isFull();

    // Check per-variant max (hardcoded caps + Skio limit, stricter wins).
    let atMax = false;
    const maxQty = this.store.getVariantMaxQty(variantId);
    if (maxQty != null && qty >= maxQty) {
      atMax = true;
    }

    // A hardcoded per-customer cap gets an explanatory tooltip on the disabled
    // + button (e.g. "1 per kunde").
    const perCustomerLabel = getPerCustomerLabel(variantId);
    const hardCap = getHardVariantCap(variantId);
    const atHardMax = hardCap != null && qty >= hardCap;
    const plusTitle = atHardMax && perCustomerLabel ? perCustomerLabel : '';

    let control = container.querySelector('.box-qty-control');
    if (control) {
      // Update existing control
      control.querySelector('.box-qty-control__qty').textContent = qty;
      const plusBtn = control.querySelector('[data-action="add"]');
      if (plusBtn) {
        plusBtn.disabled = isFull || atMax;
        if (plusTitle) {
          plusBtn.title = plusTitle;
        } else {
          plusBtn.removeAttribute('title');
        }
      }
    } else {
      // Create new control
      control = document.createElement('div');
      control.className = 'box-qty-control';
      control.innerHTML = `
        <button type="button" class="box-qty-control__btn box-qty-control__btn--minus" data-action="remove" data-variant="${variantId}" aria-label="Fjern én">\u2212</button>
        <span class="box-qty-control__qty">${qty}</span>
        <button type="button" class="box-qty-control__btn box-qty-control__btn--plus" data-action="add" data-variant="${variantId}" aria-label="Legg til én"${plusTitle ? ` title="${plusTitle}"` : ''} ${isFull || atMax ? 'disabled' : ''}>+</button>
      `;

      control.querySelectorAll('[data-action]').forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          if (btn.dataset.action === 'add') {
            this.store.addItem(btn.dataset.variant);
          } else {
            this.store.removeItem(btn.dataset.variant);
          }
        });
      });

      container.appendChild(control);
    }
  }

  _hideQtyControl(container) {
    // Restore original product-form
    const form = container.querySelector('product-form');
    if (form) form.style.display = '';

    const control = container.querySelector('.box-qty-control');
    if (control) control.remove();
  }

  // ── Flash error on the original button (full / max) ────────────────────

  _flashButton(variantId, result) {
    const messages = {
      full: 'Boksen er full!',
      max: 'Maks antall nådd',
    };

    const input = document.querySelector(
      `input.product-variant-id[value="${variantId}"], input[name="id"][value="${variantId}"]`
    );
    if (!input) return;

    const form = input.closest('form');
    if (!form) return;

    const btn = form.querySelector('.quick-add__submit, .add-to-cart__submit, .product-form__submit');
    if (!btn) return;

    const span = btn.querySelector('span > span') || btn.querySelector('span');
    if (!span) return;

    const original = span.textContent;
    span.textContent = messages[result] || '';
    btn.classList.add('button--secondary');

    setTimeout(() => {
      span.textContent = original;
      btn.classList.remove('button--secondary');
    }, 1200);
  }

  // ── Observe grid for facet-driven DOM replacement ──────────────────────

  _observeGrid() {
    const grid = document.getElementById('ProductGridContainer');
    if (!grid) return;

    let debounceTimer = null;
    this._observer = new MutationObserver(() => {
      if (this._syncing) return;
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => this._syncAllCards(), 100);
    });

    this._observer.observe(grid, { childList: true, subtree: true });
  }

  destroy() {
    document.removeEventListener('submit', this._onSubmit);
    document.removeEventListener('click', this._onClick, true);
    if (this._unsub) this._unsub();
    if (this._observer) this._observer.disconnect();
  }
}

class BoxBuilderPresetController {
  constructor(store) {
    this.store = store;
    this.drawer = document.querySelector('cart-drawer');
    this.activeContext = null;

    this._onDocumentClick = this._onDocumentClick.bind(this);
    document.addEventListener('click', this._onDocumentClick);

    this._wrapDrawerClose();
  }

  _wrapDrawerClose() {
    if (!this.drawer || this.drawer._boxBuilderPresetCloseWrapped) return;

    const originalClose = this.drawer.close.bind(this.drawer);
    this.drawer.close = (...args) => {
      this.clearDrawerContext();
      return originalClose(...args);
    };
    this.drawer._boxBuilderPresetCloseWrapped = true;
  }

  async _onDocumentClick(event) {
    const presetTrigger = event.target.closest('[data-box-preset-trigger]');
    if (presetTrigger) {
      event.preventDefault();
      const preset = this._readPresetData(presetTrigger);
      if (preset) {
        await this.applyPresetAndOpenDrawer(preset, presetTrigger);
      }
      return;
    }

    const editTrigger = event.target.closest('[data-box-preset-edit]');
    if (editTrigger) {
      event.preventDefault();
      await this.editPresetBox();
    }
  }

  _readPresetData(trigger) {
    const card = trigger.closest('.categorized-grid__preset-card');
    const dataEl = card ? card.querySelector('[data-box-preset-data]') : null;
    if (!dataEl) return null;

    try {
      return JSON.parse(dataEl.textContent);
    } catch (error) {
      console.error('BoxBuilder: failed to parse preset data', error);
      alert('Kunne ikke laste bestselgerboksen. Prøv igjen.');
      return null;
    }
  }

  _setTriggerLoading(trigger, isLoading) {
    if (!trigger) return;

    if (isLoading) {
      if (!trigger.dataset.originalLabel) {
        trigger.dataset.originalLabel = trigger.textContent.trim();
      }
      trigger.disabled = true;
      trigger.textContent = 'Legger til...';
      return;
    }

    if (trigger.dataset.originalLabel) {
      trigger.textContent = trigger.dataset.originalLabel;
    }
    trigger.disabled = false;
  }

  async applyPresetAndOpenDrawer(preset, trigger = null) {
    this._setTriggerLoading(trigger, true);

    try {
      if (this.store.itemCount() > 0) {
        const confirmed = window.confirm('Dette erstatter innholdet i boksen din. Vil du fortsette?');
        if (!confirmed) return false;
      }

      const applied = this.store.applyPreset(preset);
      if (!applied.ok) {
        alert(applied.error || 'Kunne ikke bygge bestselgerboksen. Prøv igjen.');
        return false;
      }

      if (!this.drawer || typeof this.drawer.renderContents !== 'function' || typeof this.drawer.getSectionsToRender !== 'function') {
        const ok = await this.store.addToCart();
        if (ok) {
          window.location.href = '/cart';
          return true;
        }

        alert('Kunne ikke legge boksen i handlekurven. Prøv igjen.');
        return false;
      }

      const sections = this.drawer.getSectionsToRender().map((section) => section.id);
      const response = await this.store.addToCartWithSections({
        sections,
        sectionsUrl: window.location.pathname + window.location.search,
        clearState: false,
      });

      if (!response) {
        alert('Kunne ikke legge boksen i handlekurven. Prøv igjen.');
        return false;
      }

      if (!response.sections) {
        window.location.href = '/cart';
        return true;
      }

      const lineKey = response.key || (Array.isArray(response.items) ? response.items[0]?.key : null);
      this.activeContext = {
        lineKey,
        navigateTo: window.location.pathname + window.location.search,
      };

      this.drawer.renderContents(response);
      window.requestAnimationFrame(() => this.applyDrawerContext());
      return true;
    } finally {
      this._setTriggerLoading(trigger, false);
    }
  }

  applyDrawerContext() {
    if (!this.drawer || !this.activeContext) return;

    const viewCartLink = this.drawer.querySelector('[data-cart-drawer-view-cart]');
    const checkoutButton = this.drawer.querySelector('[data-cart-drawer-checkout]');
    if (!viewCartLink || !checkoutButton) return;

    if (!viewCartLink.dataset.originalClass) {
      viewCartLink.dataset.originalClass = viewCartLink.className;
      viewCartLink.dataset.originalHref = viewCartLink.getAttribute('href') || '';
      viewCartLink.dataset.originalLabel = viewCartLink.textContent.trim();
    }
    if (!checkoutButton.dataset.originalClass) {
      checkoutButton.dataset.originalClass = checkoutButton.className;
      checkoutButton.dataset.originalLabel = checkoutButton.textContent.trim();
    }

    viewCartLink.className = 'button button-block cart-drawer__context-cta--edit';
    viewCartLink.href = this.activeContext.navigateTo || window.location.pathname;
    viewCartLink.dataset.boxPresetEdit = 'true';
    const viewCartLabel = viewCartLink.querySelector('span');
    if (viewCartLabel) {
      viewCartLabel.textContent = 'Endre på boks';
    } else {
      viewCartLink.textContent = 'Endre på boks';
    }

    checkoutButton.className = 'cart__checkout-button button button-block cart-drawer__context-cta--checkout';
    const checkoutLabel = checkoutButton.querySelector('span');
    if (checkoutLabel) {
      checkoutLabel.textContent = 'Gå til betaling';
    } else {
      checkoutButton.textContent = 'Gå til betaling';
    }
  }

  clearDrawerContext() {
    if (!this.drawer) {
      this.activeContext = null;
      return;
    }

    const viewCartLink = this.drawer.querySelector('[data-cart-drawer-view-cart]');
    const checkoutButton = this.drawer.querySelector('[data-cart-drawer-checkout]');

    if (viewCartLink && viewCartLink.dataset.originalClass) {
      viewCartLink.className = viewCartLink.dataset.originalClass;
      viewCartLink.setAttribute('href', viewCartLink.dataset.originalHref || '/cart');
      delete viewCartLink.dataset.boxPresetEdit;

      const viewCartLabel = viewCartLink.querySelector('span');
      if (viewCartLabel) {
        viewCartLabel.textContent = viewCartLink.dataset.originalLabel || '';
      } else if (viewCartLink.dataset.originalLabel) {
        viewCartLink.textContent = viewCartLink.dataset.originalLabel;
      }

      delete viewCartLink.dataset.originalClass;
      delete viewCartLink.dataset.originalHref;
      delete viewCartLink.dataset.originalLabel;
    }

    if (checkoutButton && checkoutButton.dataset.originalClass) {
      checkoutButton.className = checkoutButton.dataset.originalClass;
      const checkoutLabel = checkoutButton.querySelector('span');
      if (checkoutLabel) {
        checkoutLabel.textContent = checkoutButton.dataset.originalLabel || '';
      } else if (checkoutButton.dataset.originalLabel) {
        checkoutButton.textContent = checkoutButton.dataset.originalLabel;
      }

      delete checkoutButton.dataset.originalClass;
      delete checkoutButton.dataset.originalLabel;
    }

    this.activeContext = null;
  }

  _findTargetCartItem() {
    if (!this.drawer) return null;

    const boxItems = Array.from(this.drawer.querySelectorAll('.ravareboks-cart'));
    if (!boxItems.length) return null;

    if (!this.activeContext?.lineKey) {
      return boxItems[boxItems.length - 1];
    }

    const escapedKey = window.CSS && window.CSS.escape
      ? window.CSS.escape(this.activeContext.lineKey)
      : String(this.activeContext.lineKey).replace(/"/g, '\\"');

    return this.drawer.querySelector(`.ravareboks-cart[data-cart-item-key="${escapedKey}"]`) || boxItems[boxItems.length - 1];
  }

  async editPresetBox() {
    const cartItem = this._findTargetCartItem();
    const navigateTo = this.activeContext?.navigateTo || (window.location.pathname + window.location.search);

    if (!cartItem) {
      window.location.href = navigateTo;
      return;
    }

    const dataEl = cartItem.querySelector('.box-edit-data');
    if (!dataEl) {
      window.location.href = navigateTo;
      return;
    }

    let data;
    try {
      data = JSON.parse(dataEl.textContent);
    } catch (error) {
      console.error('BoxBuilder: failed to restore preset cart state', error);
      alert('Kunne ikke lese boksinnholdet. Last siden p\u00e5 nytt og pr\u00f8v igjen.');
      return;
    }

    const ok = await restoreBoxFromCart(data, navigateTo);
    if (!ok) {
      alert('Kunne ikke t\u00f8mme handlekurven. Last siden p\u00e5 nytt og pr\u00f8v igjen.');
    }
  }

  destroy() {
    document.removeEventListener('click', this._onDocumentClick);
  }
}

// ═══════════════════════════════════════════════════════════════════════════════
// Initialize
// ═══════════════════════════════════════════════════════════════════════════════

function initBoxBuilder() {
  const store = new BoxBuilderState();
  if (!store.ready) return;

  const header = new BoxBuilderHeader(store);
  const stickyBar = new BoxBuilderStickyBar(store);
  const interceptor = new BoxBuilderFormInterceptor(store);
  const presetController = new BoxBuilderPresetController(store);

  // Expose for external use and backward compat
  window.boxBuilder = {
    store,
    header,
    stickyBar,
    interceptor,
    presetController,
    // Convenience shortcuts
    addItem: (id, title) => store.addItem(id, title),
    removeItem: (id) => store.removeItem(id),
    applyPreset: (preset) => store.applyPreset(preset),
    openPresetDrawer: (preset) => presetController.applyPresetAndOpenDrawer(preset),
    getState: () => store.getState(),
    onStateChange: (fn) => store.onStateChange(fn),
  };
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initBoxBuilder);
} else {
  initBoxBuilder();
}
